/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
// import ProductCarouselComponent from "@eisinc/App/Components/ProductCarouselComponent/ProductCarouselComponent";
import XngCarousel, { SwiperStyle } from "@eisinc/App/Components/XngCarousel";
import { sharedCarouselStyles } from "@eisinc/App/SharedStyles/ProductCarouselStyles";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@eisinc/Overrides/Components/ProductCarouselProduct";
import { CarouselCardType } from "@eisinc/XDL/types";
import { hideElementOnPrint } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav from "@insite/content-library/Components/SkipNav";
import { BrandDetailsPageContext } from "@insite/content-library/Pages/BrandDetailsPage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    carouselType = "carouselType",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    view = "view",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    showAvailability = "showAvailability",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselType]: string;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.view]: CarouselCardType;
        [fields.showImage]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.showAvailability]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    // products: getProductsForProductInfoList(state, ownProps.id),
    state,
    pageType: getCurrentPage(state).type,
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
});

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface FeaturedProductCarouselStyles {
    carouselStyles?: SwiperStyle;
    carouselProductCondensedStyles?: ProductCarouselProductStyles;
    miniCardStyles?: ProductCarouselProductStyles;
    carouselProductStyles?: ProductCarouselProductStyles;
    titleText?: TypographyPresentationProps;
}

export const featuredProductCarouselStyles: FeaturedProductCarouselStyles = {
    carouselProductCondensedStyles: {
        ...sharedCarouselStyles.carouselProductCondensedStyles,
    },
    miniCardStyles: {
        ...sharedCarouselStyles.miniCardStyles,
    },
    carouselProductStyles: {
        ...sharedCarouselStyles.carouselProductStyles,
    },
    titleText: { variant: "h2" },
};

const styles = featuredProductCarouselStyles;

const FeaturedProductCarousel: React.FC<Props> = ({
    id,
    theme,
    fields,
    loadCarouselProducts,
    pageType,
    shellContext,
    state,
    isTabletScreen,
}) => {
    const afterCarousel = React.createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = React.useContext(BrandStateContext).value;
    const category = React.useContext(CategoryContext);
    const productContext = React.useContext(ProductContext);
    const complixId = id + (productContext?.product?.id || "") + (category?.id || "") + (brand?.id || "");
    const products = getProductsForProductInfoList(state, complixId);

    React.useEffect(() => {
        if (fields.carouselType) {
            const shouldLoad = fields.carouselType && (productContext?.product?.id || category?.id || brand?.id);
            shouldLoad &&
                loadCarouselProducts({
                    carouselId: complixId,
                    carouselType: fields.carouselType,
                    relatedProductType: "",
                    seedWithManuallyAssigned: "",
                    displayProductsFrom: "",
                    selectedCategoryIds: [],
                    numberOfProductsToDisplay: 50,
                    isProductDetailsPage,
                    productId: productContext?.product?.id,
                    isProductListPage,
                    category,
                    isBrandDetailsPage,
                    brand,
                });
        }
    }, [fields.carouselType, productContext?.product?.id, category?.id, brand?.id]);

    if (!products || products.length === 0) {
        return null;
    }

    const carouselCardStyles =
        fields.view === "condensed"
            ? styles.carouselProductCondensedStyles
            : fields.view === "miniCard" || (isTabletScreen && fields.view === "grid")
              ? styles.miniCardStyles
              : styles.carouselProductStyles;

    return (
        <>
            <StyledWrapper
                css={css`
                    ${isProductDetailsPage && hideElementOnPrint}
                `}
            >
                <SkipNav text={translate("Skip Carousel")} destination={afterCarousel} />
                <Typography {...styles.titleText}>{fields.title}</Typography>
                <XngCarousel
                    id={complixId}
                    navigation={true}
                    spaceBetween={fields.view === "condensed" ? 22 : 24}
                    scrollbar={true}
                    shadowWidth="64px"
                    showArrowsOnMobile={false}
                    extendedStyles={styles.carouselStyles}
                >
                    {products.map((product, index) => (
                        <React.Fragment key={product.id}>
                            <ProductCarouselProduct
                                carouselId={complixId}
                                product={product}
                                showImage={fields.showImage}
                                showBrand={fields.showBrandName && fields.view !== "condensed"}
                                showTitle={fields.showTitle}
                                showPartNumbers={fields.showPartNumbers && fields.view !== "condensed"}
                                showPrice={fields.showPrice}
                                showAddToCart={fields.showAddToCart}
                                showAddToList={fields.showAddToList && fields.view !== "condensed"}
                                showImageBadges={fields.showImageBadges}
                                maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                showTextBadges={fields.showTextBadges}
                                maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                view={isTabletScreen && fields.view === "grid" ? "miniCard" : fields.view}
                                showAvailability={fields.showAvailability}
                                extendedStyles={carouselCardStyles}
                            />
                        </React.Fragment>
                    ))}
                </XngCarousel>
            </StyledWrapper>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

const carouselTypeOptions = [
    {
        value: "featuredProducts",
        displayName: "Featured Category/Brand Products",
    },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(FeaturedProductCarousel))),
    definition: {
        group: "Common",
        icon: "Carousel",
        allowedContexts: ["CategoryDetailsPage", BrandDetailsPageContext, "ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.carouselType,
                displayName: "Carousel Type",
                editorTemplate: "DropDownField",
                options: carouselTypeOptions,
                defaultValue: "featuredProducts",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.view,
                displayName: "Card Type",
                editorTemplate: "RadioButtonsField",
                defaultValue: "grid",
                horizontal: true,
                fieldType: "General",
                options: [
                    {
                        displayName: "Grid",
                        value: "grid",
                    },
                    {
                        displayName: "Condensed",
                        value: "miniCard",
                    },
                    {
                        displayName: "Thumbnail",
                        value: "condensed",
                    },
                ],
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isVisible: item => item.fields[fields.view] !== "condensed",
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isVisible: item => item.fields[fields.view] !== "condensed",
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showAddToCart,
                displayName: "Add To Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showAddToList,
                displayName: "Add To List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isVisible: item => item.fields[fields.view] !== "condensed",
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
            },

            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: (widget: any) => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: (widget: any) => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
